import React, { useMemo } from "react";
import { Theme, useTheme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {useDispatch, useSelector} from 'react-redux';
import {
  Checkbox,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Typography
} from "@mui/material";
import {
  DenseIcon,
  DenseMenu,
  PromptDialog,
  TextDialog
} from "../components";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import BookmarkIcon from '@mui/icons-material/Bookmark';
import SyncIcon from '@mui/icons-material/Sync';
import LinkIcon from '@mui/icons-material/Link';
import {
  deleteBookmark,
  updateBookmark,
  Bookmark
} from "../redux/bookmarks-slice";
import { useTranslation } from "react-i18next";
import { toast } from "../app";
import { getStoredState } from ".";
import LocalScene from "../viewer/js/projections";
import { useAuth, useBookmarks, useContextMenu, useDialog, useViewer } from "../hooks";
import copyTextToClipboard from "copy-to-clipboard";
import { isStaticSite } from "../electron-modules";
import { getVisibleAssets, selectAllAssets } from "../redux/assets-slice";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    assetInfo: {
      display: 'flex',
      margin: '0px'
    },
    itemText: {
      wordBreak: "break-word",
      overflow: "hidden"
    },
    checkboxParent: {
      padding: "0px",
      height: "100%"
    },
    assetCheckbox: {
      position: "relative",
      transform: "translateY(0%)",
    }
  })
);

interface BookmarkItemProps {
  bookmark: Bookmark;
}

export const BookmarkItem = (props: BookmarkItemProps) => {
  const {bookmark} = props;

  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const {viewer} = useViewer();
  const {permissions} = useAuth();
  const {t} = useTranslation();
  const contextMenu = useContextMenu();
  const renameDialog = useDialog();
  const deleteDialog = useDialog();
  const updateDialog = useDialog();
  const {loadBookmark} = useBookmarks();

  const assets = useSelector(selectAllAssets);

  const visibleAssets = useMemo(() => {
    return getVisibleAssets(assets);
  }, [assets]);

  const handleBookmarkClick = (event) => {
    const isCheckBox = (event.target.type === "checkbox");
    if (isCheckBox) return;

    loadBookmark(bookmark.id);
  };

  const handleVisibilityClick = () => {
    dispatch(updateBookmark({
      bookmarkID: bookmark.id,
      visible: !bookmark.visible
    }));
  };

  const handleDeleteSubmit = () => {
    dispatch(deleteBookmark(bookmark.id));
    deleteDialog.handleClose();
  };

  const handleDeleteClick = () => {
    contextMenu.handleClose();
    deleteDialog.handleOpen();
  };

  const handleRenameSubmit = name => {
    dispatch(updateBookmark({
      bookmarkID: bookmark.id,
      name
    }));

    renameDialog.handleClose();
  };

  const handleRenameClick = () => {
    contextMenu.handleClose();
    renameDialog.handleOpen();
  };

  const handleUpdateSubmit = () => {
    updateDialog.handleClose();

    if (!LocalScene.initialized) {
      toast.warning(t('bookmarks.invalid_position'));
      return;
    }

    const position = viewer.getCameraPosition().toArray();
    const {settings, aerialState, sceneState} = getStoredState();
    const visibleAssetIDs = visibleAssets.map(asset => asset.id);

    dispatch(updateBookmark({
      bookmarkID: bookmark.id,
      position,
      settings,
      visibleAssets: visibleAssetIDs,
      aerialState,
      sceneState
    }));

    toast.success(t('bookmarks.updated_bookmark'));
  };

  const handleUpdateClick = () => {
    contextMenu.handleClose();
    updateDialog.handleOpen();
  };

  const handleCopyClick = () => {
    contextMenu.handleClose();

    // Copy url to clipboard
    const url = new URL(window.location as any);
    url.hash = `bookmark=${bookmark.id}`;
    copyTextToClipboard(url.href);
    toast.success(t("toast.copied-to-clipboard"));
  };

  return (
    <React.Fragment>
      <ListItemButton
        style={{
          paddingLeft: theme.spacing(4),
          display: "block"
        }}
        onClick={handleBookmarkClick}
        onContextMenu={contextMenu.handleOpen}
      >
        <div className={classes.assetInfo}>
          <DenseIcon centered>
            <BookmarkIcon fontSize="small"/>
          </DenseIcon>

          <ListItemText
            className={classes.itemText}
            primary={bookmark.name}
          />

          <ListItemSecondaryAction
            className={classes.assetCheckbox}
          >
            <Checkbox
              edge="end"
              className={classes.checkboxParent}
              onChange={handleVisibilityClick}
              checked={bookmark.visible}
            />
          </ListItemSecondaryAction>

        </div>
      </ListItemButton>

      <DenseMenu
        open={contextMenu.open}
        onClose={contextMenu.handleClose}
        anchorPosition={contextMenu.anchorPosition}
      >
        {isStaticSite && <MenuItem onClick={handleCopyClick}>
          <DenseIcon>
            <LinkIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">{t('bookmarks.copy_bookmark_url')}</Typography>
        </MenuItem>}

        <MenuItem
          onClick={handleRenameClick}
          disabled={!permissions.edit}
        >
          <DenseIcon>
            <EditIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">{t('buttons.rename')}</Typography>
        </MenuItem>

        <MenuItem
          onClick={handleUpdateClick}
          disabled={!permissions.edit}
        >
          <DenseIcon>
            <SyncIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">{t('buttons.update')}</Typography>
        </MenuItem>

        <MenuItem
          onClick={handleDeleteClick}
          disabled={!permissions.delete}
        >
          <DenseIcon>
            <DeleteIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">{t('buttons.delete')}</Typography>
        </MenuItem>

      </DenseMenu>

      {/* Rename Dialog */}
      <TextDialog
        open={renameDialog.open}
        onCancel={renameDialog.handleClose}
        onSubmit={handleRenameSubmit}
        title={t('asset.rename-title')}
        prompt={t('asset.rename-prompt')}
        placeholder={bookmark.name}
        label={t('asset.rename-label')}
      />

      {/* Update Dialog */}
      <PromptDialog
        open={updateDialog.open}
        onCancel={updateDialog.handleClose}
        onSubmit={handleUpdateSubmit}
        title={t('bookmarks.update_bookmark')}
        prompt={t('bookmarks.do_you_want_to_update_this_bookmark')}
        button={t('buttons.update')}
      />

      {/* Delete Dialog */}
      <PromptDialog
        open={deleteDialog.open}
        onCancel={deleteDialog.handleClose}
        onSubmit={handleDeleteSubmit}
        title={t('bookmarks.delete_bookmark')}
        prompt={t('bookmarks.do_you_want_to_delete_this_bookmark')}
        button={t("buttons.delete")}
      />
    </React.Fragment>
  );
};